import { pinnedContracts } from '@hoodmorning/config';
import { SELECTED_ACCOUNT_CHAIN_ID } from '@hoodmorning/contracts';
import { createPublicClient, custom, decodeFunctionData, encodeFunctionData, keccak256, toHex, type Hex, type PublicClient } from 'viem';
import { entryPoint07Abi } from 'viem/account-abstraction';
import { z } from 'zod';
import { counterfactualProbeAbi } from './counterfactualSimulation.js';
import { terminalPreparationDiagnosticMessage } from './terminalPreparationDiagnostic.js';

const quantity = z.string().regex(/^0x(?:0|[1-9a-fA-F][0-9a-fA-F]{0,63})$/);
const hash = z.string().regex(/^0x[0-9a-fA-F]{64}$/);
const bytes = z.string().regex(/^0x(?:[0-9a-fA-F]{2})*$/);
const headerSchema = z.object({ number: quantity, hash, parentHash: hash, timestamp: quantity }).passthrough();
type Header = z.infer<typeof headerSchema>;
export type CanonicalReadPort = {
  request(args: { method: string; params: unknown[] }, options: { retryCount: 0 }): Promise<unknown>;
};
type CanonicalFailureReason = 'source-age' | 'source-future' | 'read-budget' | 'transport'
  | 'chain-mismatch' | 'header-mismatch' | 'source-mismatch';
// Fixed labels only; a recognized structured terminal diagnostic may be rendered
// separately. Never attach provider errors, causes or request/source contents.
const fail = (reason?: CanonicalFailureReason) => new Error(`Canonical block observation failed${reason ? ` [${reason}]` : ''}`);
const sameHeader = (a: Header, b: Header) => a.hash.toLowerCase() === b.hash.toLowerCase()
  && a.parentHash.toLowerCase() === b.parentHash.toLowerCase()
  && BigInt(a.number) === BigInt(b.number) && BigInt(a.timestamp) === BigInt(b.timestamp);
const address = z.string().regex(/^0x[0-9a-fA-F]{40}$/).refine(value => BigInt(value) !== 0n);
const probeTransactionSchema = z.strictObject({ from: address, to: address, data: bytes,
  gas: quantity, value: z.literal('0x0') });
const probeSchema = z.strictObject({ transaction: probeTransactionSchema,
  helper: z.strictObject({ address, runtime: bytes, codeHash: hash }) });
export type CanonicalCounterfactualProbe = z.infer<typeof probeSchema>;

function capturedProbe(value: CanonicalCounterfactualProbe) {
  const probe = probeSchema.parse(structuredClone(value));
  if (probe.transaction.to.toLowerCase() !== pinnedContracts.entryPointV07.address.toLowerCase()
    || BigInt(probe.transaction.gas) < 100_000n || BigInt(probe.transaction.gas) > 50_000_000n
    || BigInt(probe.helper.address) <= 65535n
    || [probe.transaction.from, probe.transaction.to].some(value => value.toLowerCase() === probe.helper.address.toLowerCase())
    || probe.helper.runtime.length <= 2 || probe.helper.runtime.length > 49154
    || keccak256(probe.helper.runtime as Hex).toLowerCase() !== probe.helper.codeHash.toLowerCase()) throw fail();
  const outer = decodeFunctionData({ abi: entryPoint07Abi, data: probe.transaction.data as Hex });
  if (outer.functionName !== 'delegateAndRevert' || outer.args[0].toLowerCase() !== probe.helper.address.toLowerCase()
    || encodeFunctionData({ abi: entryPoint07Abi, functionName: outer.functionName, args: outer.args }).toLowerCase()
      !== probe.transaction.data.toLowerCase()) throw fail();
  const inner = decodeFunctionData({ abi: counterfactualProbeAbi, data: outer.args[1] });
  if (inner.functionName !== 'simulate' || inner.args[0].owner.toLowerCase() !== probe.transaction.from.toLowerCase()
    || inner.args[0].sender.toLowerCase() === probe.helper.address.toLowerCase()
    || encodeFunctionData({ abi: counterfactualProbeAbi, functionName: inner.functionName, args: inner.args }).toLowerCase()
      !== outer.args[1].toLowerCase()) throw fail();
  return probe;
}

/** Read-only EIP-1898 snapshot for the conditional-order preflight composition.
 * The port must separately bound network duration/bytes and honor no retries.
 * No provider failover, latest-state fallback, overrides, signing or sends.
 * A snapshot is not finality, registration provenance or permission to execute.
 * Freshness values come from the controlled fixture / reviewed server policy,
 * never a public order request. Caller MUST await verifySource after its reads.
 */
export async function createCanonicalBlockReadClient(input: Parameters<typeof createBlockReadClient>[0]) {
  return createBlockReadClient(input, false);
}

/** Execution-only counterfactual probe: normal observations retain all canonical
 * guards. The sole exception is this exact captured EntryPoint delegateAndRevert
 * transaction with one independently pinned helper code override. No arbitrary
 * account/storage/balance override, block override, fallback, signature or send.
 * A provider's revert is preserved only on that exact read so the existing
 * simulator can independently validate its canonical result envelope. Caller
 * must select reviewed inputs, share the invocation budget and verifySource. */
export async function createCounterfactualCanonicalBlockReadClient(input:
  Omit<Parameters<typeof createBlockReadClient>[0], 'source'> & {
    source: NonNullable<Parameters<typeof createBlockReadClient>[0]['source']>;
    probe: CanonicalCounterfactualProbe;
  }) {
  if (!input.source) throw fail();
  const probe = capturedProbe(input.probe);
  return createBlockReadClient(input, false, probe);
}

/** Historical valuation only: an explicit canonical source is mandatory.
 * Old block age is permitted, but collection duration, future timestamps and
 * every canonical/EIP-1898 guard remain enforced. This never authorizes trading. */
export async function createHistoricalCanonicalBlockReadClient(input: {
  port: CanonicalReadPort;
  source: {number: bigint; hash: Hex; timestamp: bigint};
  policy: {maxRequests: number; maxObservationAgeSeconds: number; maxFutureSeconds: number};
  now?: () => number;
  /** Independent final header/chain reads may share a transport envelope. Both
   * still reach the provider; no source result is reused. Default unchanged. */
  parallelSourceChecks?: boolean;
}) {
  if (!input.source) throw fail();
  if (input.parallelSourceChecks !== undefined && typeof input.parallelSourceChecks !== 'boolean') throw fail();
  return createBlockReadClient({...input,policy:{maxRequests:input.policy.maxRequests,
    maxAgeSeconds:input.policy.maxObservationAgeSeconds,maxFutureSeconds:input.policy.maxFutureSeconds}},true,
    undefined,input.parallelSourceChecks===true);
}

async function createBlockReadClient(input: {
  port: CanonicalReadPort;
  policy: { maxRequests: number; maxAgeSeconds: number; maxFutureSeconds: number };
  /** Continue a genuine prior observation at its exact canonical source, not
   * a mixture of that observation and a later `latest` block. Still subject to
   * numbered-header identity, EIP-1898, freshness and final source checks. */
  source?: { number: bigint; hash: Hex; timestamp: bigint };
  now?: () => number;
}, historical: boolean, probe?: CanonicalCounterfactualProbe, parallelSourceChecks = false): Promise<{
  client: PublicClient;
  source: { number: bigint; hash: Hex; timestamp: bigint };
  verifySource(): Promise<{ sourceBlockNumber: bigint; sourceBlockHash: Hex; executionAuthorized: false; finalityProven: false }>;
}> {
  const send = input.port.request.bind(input.port), now = input.now ?? Date.now;
  const policy = structuredClone(input.policy);
  const requested = input.source === undefined ? undefined : z.strictObject({
    number: z.bigint().min(0n).max((1n << 256n) - 1n), hash,
    timestamp: z.bigint().min(0n).max((1n << 256n) - 1n),
  }).parse(structuredClone(input.source));
  if (!Number.isSafeInteger(policy.maxRequests) || policy.maxRequests < 4 || policy.maxRequests > 128
    || !Number.isSafeInteger(policy.maxAgeSeconds) || policy.maxAgeSeconds < 1
    || !Number.isSafeInteger(policy.maxFutureSeconds) || policy.maxFutureSeconds < 0) throw fail();
  const startedAt = historical ? now() : null;
  function historicalDeadline() {
    if (!historical) return;
    const time = now();
    if (!Number.isSafeInteger(startedAt) || startedAt! < 0 || !Number.isSafeInteger(time) || time < startedAt!
      || BigInt(time) - BigInt(startedAt!) > BigInt(policy.maxAgeSeconds) * 1000n) throw fail();
  }
  let requests = 0;
  async function read(method: string, params: unknown[], preserveProbeError = false) {
    historicalDeadline();
    if (++requests > policy.maxRequests) throw fail('read-budget');
    let received = false;
    try { const result = await send({ method, params }, { retryCount: 0 }); received = true; historicalDeadline(); return result; }
    catch (error) { historicalDeadline(); if (preserveProbeError) throw error;
      // Preserve generic historical-deadline failures without mislabelling them as transport.
      const refusal = fail(received ? undefined : 'transport');
      const diagnostic = received ? undefined : terminalPreparationDiagnosticMessage(error);
      if (diagnostic) refusal.message += ` ${diagnostic}`;
      throw refusal; }
  }
  async function chain() {
    if (BigInt(quantity.parse(await read('eth_chainId', []))) !== BigInt(SELECTED_ACCOUNT_CHAIN_ID)) throw fail('chain-mismatch');
  }
  await chain();
  const bound = headerSchema.parse(await read('eth_getBlockByNumber', [requested ? toHex(requested.number) : 'latest', false]));
  if (requested && (BigInt(bound.number) !== requested.number || BigInt(bound.timestamp) !== requested.timestamp
    || bound.hash.toLowerCase() !== requested.hash.toLowerCase())) throw fail('source-mismatch');
  const number = BigInt(bound.number), tag = toHex(number);
  function fresh() {
    historicalDeadline();
    const time = now();
    if (!Number.isSafeInteger(time) || time < 0) throw fail();
    const seconds = BigInt(Math.floor(time / 1000)), timestamp = BigInt(bound.timestamp);
    if (!historical && seconds > timestamp + BigInt(policy.maxAgeSeconds)) throw fail('source-age');
    if (timestamp > seconds + BigInt(policy.maxFutureSeconds)) throw fail('source-future');
  }
  fresh();
  async function header() {
    const current = headerSchema.parse(await read('eth_getBlockByNumber', [tag, false]));
    if (!sameHeader(current, bound)) throw fail('header-mismatch');
    return current;
  }
  const client = createPublicClient({ cacheTime: 0, ccipRead: false, transport: custom({ request: async ({ method, params }) => {
    fresh();
    const p: unknown[] = Array.isArray(params) ? [...params] : [];
    if (method === 'eth_chainId') {
      if (p.length !== 0) throw fail();
      await chain(); fresh(); return toHex(SELECTED_ACCOUNT_CHAIN_ID);
    }
    if (method === 'eth_getBlockByNumber') {
      if (p.length !== 2 || p[0] !== tag || p[1] !== false) throw fail();
      const value = await header(); fresh(); return value;
    }
    if (method === 'eth_call' && p.length === 3 && probe) {
      const transaction = probeTransactionSchema.parse(p[0]);
      if (p[1] !== tag || Object.entries(transaction).some(([key, value]) =>
        value.toLowerCase() !== probe.transaction[key as keyof typeof transaction].toLowerCase())) throw fail();
      const overrides = z.record(z.string(), z.strictObject({ code: bytes })).parse(p[2]);
      const entries = Object.entries(overrides);
      if (entries.length !== 1 || entries[0]![0].toLowerCase() !== probe.helper.address.toLowerCase()
        || entries[0]![1].code.toLowerCase() !== probe.helper.runtime.toLowerCase()) throw fail();
      const args = [structuredClone(probe.transaction), { blockHash: bound.hash, requireCanonical: true },
        { [probe.helper.address]: { code: probe.helper.runtime } }];
      try { const value = bytes.parse(await read(method, args, true)); fresh(); return value; }
      catch (error) { fresh(); throw error; }
    }
    const index = method === 'eth_getStorageAt' ? 2 : 1;
    if (!['eth_getCode', 'eth_getStorageAt', 'eth_getBalance', 'eth_getTransactionCount', 'eth_call'].includes(method)
      || p.length !== index + 1 || p[index] !== tag) throw fail();
    // Length check also rejects state/block override arguments to eth_call.
    p[index] = { blockHash: bound.hash, requireCanonical: true };
    const raw = await read(method, p);
    const value = (method === 'eth_getBalance' || method === 'eth_getTransactionCount' ? quantity
      : method === 'eth_getStorageAt' ? hash : bytes).parse(raw);
    fresh(); return value;
  } }, { retryCount: 0 }) });
  return {
    client,
    source: { number, hash: bound.hash as Hex, timestamp: BigInt(bound.timestamp) },
    async verifySource() {
      fresh();
      if (parallelSourceChecks) await Promise.all([header(), chain()]);
      else { await header(); await chain(); }
      fresh();
      return { sourceBlockNumber: number, sourceBlockHash: bound.hash as Hex,
        executionAuthorized: false as const, finalityProven: false as const };
    },
  };
}
